import type { Cart } from '@/modules/cart/domain/Cart';
import type { CartLineItem } from '@/modules/cart/domain/CartLineItem';
import type { CartProductOption } from '@/modules/cart/domain/CartProductOption';

import {
  EAnalyticsShippingMethodType,
  type IWebViewGtmProduct
} from '../types';

const getAttributeValue = (
  item: CartLineItem,
  attributeName: string
): string => {
  const attr = item?.variant?.attributes?.find(
    (attributeItem) => attributeItem.name === attributeName
  );

  if (!attr) return '';

  if (typeof attr.value === 'string') {
    return attr.value;
  }

  if (typeof attr.value === 'object') {
    if (Array.isArray(attr.value)) {
      return attr.value.join(',');
    }
    return attr.value.key ?? '';
  }

  return '';
};

const getItemCategory2 = (item: CartLineItem) => {
  const attributes = Object.fromEntries(
    item?.variant?.attributes?.map((attr) => [attr.name, attr.value]) || []
  );

  const departmentName = attributes['departmentName'] ?? '';
  const className = attributes['className'] ?? '';
  const subDepartmentName = attributes['subdepartmentName'] ?? '';

  if (!departmentName && !className && !subDepartmentName) {
    return '';
  }

  return departmentName + '/' + className + '/' + subDepartmentName;
};

const getItemCategory3 = (item: CartLineItem) => {
  const itemPrice = item.price.value.centAmount;
  return itemPrice ? itemPrice.toString() : '0';
};

const getItemCategory4 = (item: CartLineItem, cart: Cart) => {
  const deliveryGroups = cart?.deliveryInfo?.deliveryGroups ?? [];

  const deliveryGroup = deliveryGroups.find((group) =>
    group.products.includes(item.variant.sku)
  );

  const shippingType = deliveryGroup?.shippingOption?.shippingType;
  const shippingMethod = deliveryGroup?.shippingOption?.shippingMethod;
  const isCollect = shippingType === 'COLLECT';
  const isSameDay = shippingMethod === 'SAME_DAY';
  const isNextDay = shippingMethod === 'NEXT_DAY';
  const isDateRange = shippingMethod === 'DATE_RANGE';
  const isScheduled = shippingMethod === 'SCHEDULED';

  if (isCollect) return EAnalyticsShippingMethodType.COLLECT;
  if (isSameDay) return EAnalyticsShippingMethodType.SAME_DAY;
  if (isNextDay) return EAnalyticsShippingMethodType.NEXT_DAY;
  if (isDateRange) return EAnalyticsShippingMethodType.DATE_RANGE;
  if (isScheduled) return EAnalyticsShippingMethodType.SCHEDULED;
  return '';
};

const getItemCategory5 = (
  item: CartLineItem,
  productOptions: CartProductOption[]
) => {
  return (
    productOptions.find(
      (option) =>
        option.productSku === item.variant.sku &&
        option.addonType === 'warranty'
    )?.name || ''
  );
};

export const createGTMProductWebViewObject = (
  cart: Cart
): IWebViewGtmProduct[] => {
  const items = cart.lineItems ?? [];
  const productOptions = cart?.productOptions ?? [];

  return items.map((item) => {
    const { offer, regular } = item?.variant?.prices || {};
    const price = offer || regular;

    const itemCategory2 = getItemCategory2(item);
    const itemCategory3 = getItemCategory3(item);
    const itemCategory4 = getItemCategory4(item, cart);
    const itemCategory5 = getItemCategory5(item, productOptions);
    const itemBrand = getAttributeValue(item, 'brand') ?? '';

    const productWebView = {
      AnalyticsParameterItemID: item?.variant?.sku || '',
      AnalyticsParameterItemName: item?.name || '',
      AnalyticsParameterItemCategory: item?.productType?.key || '',
      AnalyticsParameterItemCategory2: itemCategory2,
      AnalyticsParameterItemCategory3: itemCategory3,
      AnalyticsParameterItemCategory4: itemCategory4,
      AnalyticsParameterItemCategory5: itemCategory5,
      AnalyticsParameterItemBrand: itemBrand,
      AnalyticsParameterPrice: price?.value?.centAmount || 0,
      AnalyticsParameterQuantity: item?.quantity || 1
    };

    return productWebView;
  });
};
